/**
* @summary - Calibration Create and Edit Form
* @author Yuvasri S
* @since 2024-12-17
*/
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Grid, Button } from "@mui/material";
import utils from "../../utility/utilities";
import { getFacilityId } from "../../utility/tokenUtility";
import HomeIconButton from '../commoncomponents/HomeIconButton';
import CustomDialog from "../commoncomponents/CustomDialog";
import CustomTextField from "../commoncomponents/CustomTextField";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import PageTitle from "../commoncomponents/PageTitle";
import calibrationCertificateRepository from '../../api/repositories/calibrationCertificateRepository';
import moment from "moment";
import { useLocation } from "react-router-dom";


const CalibrationCertificateForm = forwardRef((props, ref) => {
    const location = useLocation();
    const editMode = location?.state?.editmode;
    const viewMode = location?.state?.viewmode;
    const facilityId = getFacilityId('facilityId');

    const { onCalibrationCategoryChange } = props;

    const [serviceOptions, setServiceOptions] = useState([]);

    const [calibrationcategoryOptions, setCalibrationcategoryOptions] = useState([]);
    const [hospitalNameOptions, setHospitalNameOptions] = useState([]);
    const [calibrationTypecodeOptions, setCalibrationTypeCodeOptions] = useState([]);


    const currentDate = utils.getCurrentDateFormatted("DD-MM-YYYY");
    const [openAlertDialog, setOpenAlertDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    const [errormessages, setErrorMessages] = useState('');


    const active = {
        label: "Active",
        value: "Active",
        id: true,
    };
    const inActive = {
        label: "Inactive",
        value: "Inactive",
        id: false,
    },
        statusOptions = [active, inActive];

    const [formState, setFormState] = useState({
        service: '',
        calibrationNo: '',
        calibrationCategory: '',
        hospitalName: '',
        calibrationTypeCode: '',
        calibrationTypeCodeDesc: '',
        status: active,
        classGrade: '',
        inspectedBy: '',
        contactPerson: '',
        inspectionSubmittedDate: '',
        inspectedConductOn: '',
        nextInspectionDate: '',
        expiryDate: '',
        issuingBody: '',
        issueDate: '',
        remarks: '',

    });
    const serviceType = formState.service.value === 'FEMS' ? 'fem' : 'bem';
    const [errors, setErrors] = useState({});
    const calculateMinimumDate = (date) => moment(date).add(1, 'days').format('DD/MM/YYYY');
    const [isDirty, setIsDirty] = useState(false); //unsaved changes
    const [hospital, setHospital] = useState('');
    const formatDateForDisplay = (date) => {
        if (!date) {
            return '';
        }
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };
    const handleCloseAlertDialog = () => {
        setOpenAlertDialog(false);
    };

    const formatDateForBackend = (date) => {
        let dayin = !!date ? new Date(date) : null;

        if (dayin) {
            if (isNaN(dayin.getTime())) {
                console.error('Invalid Date:', date);
            } else {

                let dateformat = moment(dayin).unix() * 1000

                let Monthyear = moment(dateformat).format('YYYY-MM-DD HH:mm:ss');

                return Monthyear

            }
        }
    };
    const validateDate = (date, comparisonDate, comparisonType, granularity = 'day') => {
        switch (comparisonType) {
            case 'isAfter':
                return date.isAfter(comparisonDate, granularity);
            case 'isBefore':
                return date.isBefore(comparisonDate, granularity);
            case 'isSame':
                return date.isSame(comparisonDate, granularity);
            case 'isSameOrAfter':
                return date.isSameOrAfter(comparisonDate, granularity);
            case 'isSameOrBefore':
                return date.isSameOrBefore(comparisonDate, granularity);
            default:
                return false;
        }
    };

    const handleDateChange = (field, newDate) => {
        setIsDirty(true);
        if (newDate) {
            const momentDate = moment(newDate, 'DD/MM/YYYY', true);

            const inspectedMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const submittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();

            if (!momentDate.isValid()) return;

            const formattedDate = momentDate.format('YYYY-MM-DDTHH:mm:ss');
            let newErrors = { ...errors };

            const setAlert = (message, field) => {
                newErrors[field] = true;
                setAlertMessage(message);
                setOpenAlertDialog(true);
            };

            switch (field) {
                case 'inspectionSubmittedDate':
                    if (validateDate(momentDate, currentMoment, 'isAfter')) {
                        setAlert('Submitted date for Inspection cannot be in the future.', 'inspectionSubmittedDate');
                    } else if (formState.inspectedConductOn && validateDate(momentDate, inspectedMoment, 'isAfter')) {
                        setAlert('Submitted date for Inspection cannot be after the Inspection Conducted date.', 'inspectionSubmittedDate');
                    } else {
                        newErrors.inspectionSubmittedDate = false;
                    }

                    setFormState((prev) => ({ ...prev, inspectionSubmittedDate: formattedDate }));
                    break;

                case 'inspectedConductOn':
                    if (validateDate(momentDate, currentMoment, 'isAfter')) {
                        setAlert('Inspection Conducted date cannot be in the future.', 'inspectedConductOn');
                    } else if (formState.inspectionSubmittedDate && validateDate(momentDate, submittedMoment, 'isBefore')) {
                        setAlert('Inspection Conducted date must not be before the Submitted date for Inspection.', 'inspectedConductOn');
                    } else if (formState.nextInspectionDate && validateDate(momentDate, nextInspectionMoment, 'isSame')) {
                        setAlert('Inspection Conducted date should not be the same as the Next Inspection Date.', 'inspectedConductOn');
                    } else if (formState.nextInspectionDate && validateDate(momentDate, nextInspectionMoment, 'isAfter')) {
                        setAlert('Inspection Conducted date should not be after the Next Inspection Date.', 'inspectedConductOn');
                    } else {
                        newErrors.inspectedConductOn = false;
                    }

                    setFormState((prev) => ({ ...prev, inspectedConductOn: formattedDate }));
                    break;

                case 'nextInspectionDate':
                    if (formState.inspectedConductOn && validateDate(momentDate, inspectedMoment, 'isSameOrBefore')) {
                        setAlert('Next Inspection Date must be after the Inspection Conducted date.', 'nextInspectionDate');
                    } else {
                        newErrors.nextInspectionDate = false;
                    }

                    setFormState((prev) => ({ ...prev, nextInspectionDate: formattedDate }));
                    break;

                case 'expiryDate':
                    if (validateDate(momentDate, currentMoment, 'isBefore')) {
                        setAlert('Expiry date cannot be in the past.', 'expiryDate');
                        setFormState((prev) => ({ ...prev, expiryDate: '' }));
                    } else {
                        setFormState((prev) => ({
                            ...prev,
                            expiryDate: formattedDate,
                            formattedExpiryDate: momentDate.format('DD-MM-YYYY')
                        }));
                        newErrors.expiryDate = false;
                    }
                    break;

                case 'issueDate':
                    setFormState((prev) => ({
                        ...prev,
                        issueDate: formattedDate
                    }));
                    break;

                default:
                    break;
            }
            setErrors(newErrors);
        }
    };

    useEffect(() => {
        if (editMode) return; // Skip execution if in edit mode
        if (viewMode) return;
        const getLov = async () => {
            try {
                const services = await calibrationCertificateRepository.fetchServiceOptions();

                setServiceOptions(services);
                if (services.length > 0) {
                    handleOnChange(services[1], "service");
                }
                const calibrationCategorys = await calibrationCertificateRepository.fetchCalibrationCategoryOptions();
                setCalibrationcategoryOptions(calibrationCategorys);
                if (calibrationCategorys.length > 0) {
                    handleOnChange(calibrationCategorys[0], "calibrationCategory");
                }

                const hospitalName = await calibrationCertificateRepository.fetchHospitalName();
                setHospitalNameOptions(hospitalName);

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        getLov();
    }, []);

    useEffect(() => {
        const fetchCalibrationTypeCodeOptions = async () => {
            if (formState?.service?.id && formState?.calibrationCategory?.id) {
                try {
                    const calibrationTypeCode = await calibrationCertificateRepository.fetchcalibrationTypeCodeOptions(
                        formState?.service.id,
                        formState.calibrationCategory.id
                    );
                    setCalibrationTypeCodeOptions(calibrationTypeCode);
                } catch (error) {
                    console.error("Error fetching calibration type code options:", error);
                }
            } else {
                setCalibrationTypeCodeOptions([]); // Clear options if dependencies are not set
            }
        };

        fetchCalibrationTypeCodeOptions();
    }, [formState?.calibrationCategory, formState?.service]);


    useEffect(() => {
        if (props.onCalibrationCategoryChange) {
            props.onCalibrationCategoryChange(formState.calibrationCategory);
        }
    }, [formState.calibrationCategory]);


    useEffect(() => {
        if (location?.state) {
            const { serviceId, service, caliblicenseno, categoryprimaryid, categoryName, licenseId, licntypecode,
                licntypevalue, calibclassgrade, isactive, isactiveformate, calibinspectedby, calibcontactperson, calibsubmitteddate, calibinspectiondate,
                calibnextinspdate, calibexpirydate, calibissuingbody, calibissuedate, calibremarks, categorydesc } = location.state

            const serviceVal = { id: serviceId, label: service, value: service }
            const categoryVal = { primaryId: categoryprimaryid, label: categoryName, value: categoryName }
            const typeCodeVal = { id: licenseId, label: licntypecode, value: licntypevalue }
            const statusVal = { id: isactive, label: isactiveformate, value: isactiveformate }
            const hospitalVal = { label: categorydesc, value: categorydesc }
            setFormState({
                service: serviceVal, calibrationNo: caliblicenseno, calibrationCategory: categoryVal,
                calibrationTypeCode: typeCodeVal, calibrationTypeCodeDesc: licntypevalue, classGrade: calibclassgrade,
                status: statusVal, inspectedBy: calibinspectedby, contactPerson: calibcontactperson, inspectionSubmittedDate: calibsubmitteddate,
                inspectedConductOn: calibinspectiondate, nextInspectionDate: calibnextinspdate, expiryDate: calibexpirydate,
                issuingBody: calibissuingbody, issueDate: calibissuedate, remarks: calibremarks, hospitalName: hospitalVal
            })
            setHospital(categoryName);
        }

    }, [location?.state])

    const handleNumericValue = (value) => {
        const numericValue = value.replace(/[^0-9]/g, "");
        return numericValue.slice(0, 13);
    };

    const handleOnChange = (values, name) => {
        let processedValue = values || "";

        // Handle numeric value processing
        if (name === "telephoneNo" || name === "faxNo") {
            processedValue = handleNumericValue(values);
        }

        setFormState((prev) => {
            const updatedState = { ...prev, [name]: processedValue };

            // Clear related fields when service is changed
            if (name === "service") {
                updatedState.calibrationCategory = ""; // Clear calibrationCategory
                updatedState.calibrationTypeCode = ""; // Clear calibrationTypeCode
                updatedState.calibrationTypeCodeDesc = ""; // Clear calibrationTypeCodeDesc
            }

            // Clear related fields when calibrationCategory is changed
            if (name === "calibrationCategory") {
                updatedState.calibrationTypeCode = ""; // Clear calibrationTypeCode
                updatedState.calibrationTypeCodeDesc = ""; // Clear calibrationTypeCodeDesc
                updatedState.hospitalName = "";
                if (onCalibrationCategoryChange) {
                    onCalibrationCategoryChange(processedValue);
                    setHospital(processedValue); // Update hospital logic if needed
                }
            }
            if (name === "calibrationTypeCode") {
                updatedState.calibrationTypeCodeDesc = processedValue.value || ""; // Clear calibrationTypeCodeDesc
            }

            // Set status to active if other fields are being updated
            if (name !== "status" && prev.status?.value !== "Active") {
                updatedState.status = {
                    label: "Active",
                    value: "Active",
                    id: true,
                };
            }

            return updatedState;
        });

        setIsDirty(true);

        // Validation logic
        setErrors((prev) => {
            let errorMessage = null;

            // Required field validation for specific fields
            if (
                ["service", "calibrationNo", "calibrationTypeCode", "calibrationCategory", "expiryDate"].includes(name) &&
                !processedValue
            ) {
                errorMessage = `${name === "service"
                    ? "Service"
                    : name === "calibrationNo"
                        ? "Calibration No."
                        : name === "calibrationTypeCode"
                            ? "Calibration Type Code"
                            : name === "calibrationCategory"
                                ? "Calibration Category"
                                : name === "expiryDate"
                                    ? "Expiry Date"
                                    : ""
                    } is required.`;
            }

            // Additional validation for hospitalName when calibrationCategory.value is 'Hospital'
            if (
                name === "calibrationCategory" &&
                values?.value === "Hospital" &&
                !formState?.hospitalName
            ) 
            // {
            //     return {
            //         ...prev,
            //         hospitalName: "Hospital Name is required.",
            //     };
            // }

            // If hospitalName is being updated, validate its requirement
            if (name === "hospitalName" && !processedValue) {
                errorMessage = "Hospital Name is required.";
            }

            return { ...prev, [name]: errorMessage };
        });
    };

    const validateFields = async () => {
        const requiredFields = ['service', 'calibrationNo', 'calibrationTypeCode', 'calibrationCategory', 'expiryDate'];
        const newErrors = {};

        // Validate required fields
        requiredFields.forEach((field) => {
            if (
                !formState[field] ||
                (field === 'calibrationTypeCode' && !formState[field].label) ||
                (field === 'calibrationCategory' && !formState[field].value) ||
                (field === 'expiryDate' && !formState[field])
            ) {
                newErrors[field] = `${field === 'service'
                    ? 'Service'
                    : field === 'calibrationNo'
                        ? 'Calibration No.'
                        : field === 'calibrationTypeCode'
                            ? 'Calibration Type Code'
                            : field === 'calibrationCategory'
                                ? 'Calibration Category'
                                : field === 'expiryDate'
                                    ? 'Expiry Date'
                                    : ''
                    } is required.`;
            }
        });

        // Additional validation for hospitalName when calibrationCategory.value === 'Hospital'
        if (formState?.calibrationCategory?.value === 'Hospital' && !formState.hospitalName?.label) {
            newErrors.hospitalName = 'Hospital Name is required.';
        }

        // Validate inspectedConductOn
        if (formState.inspectedConductOn) {
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectionSubmittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);

            if (inspectedConductMoment.isAfter(currentMoment, 'day')) {
                newErrors.inspectedConductOn = 'Inspection Conducted date cannot be in the future.';
            } else if (
                formState.nextInspectionDate &&
                nextInspectionMoment.isValid() &&
                inspectedConductMoment.isSameOrAfter(nextInspectionMoment, 'day')
            ) {
                newErrors.inspectedConductOn = 'Inspection Conducted date cannot be on or after the Next Inspection Date.';
            } else if (
                formState.inspectionSubmittedDate &&
                inspectionSubmittedMoment.isValid() &&
                inspectedConductMoment.isBefore(inspectionSubmittedMoment, 'day')
            ) {
                newErrors.inspectedConductOn =
                    'Inspection Conducted date must not be before the Submitted date for Inspection.';
            }
        }

        // Validate nextInspectionDate
        if (formState.nextInspectionDate) {
            const nextInspectionMoment = moment(formState.nextInspectionDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);

            if (!nextInspectionMoment.isValid()) {
                newErrors.nextInspectionDate = 'Invalid Next Inspection Date.';
            } else if (
                formState.inspectedConductOn &&
                inspectedConductMoment.isValid() &&
                nextInspectionMoment.isSameOrBefore(inspectedConductMoment, 'day')
            ) {
                newErrors.nextInspectionDate = 'Next Inspection Date must be after the Inspection Conducted date.';
            }
        }

        // Validate inspectionSubmittedDate
        if (formState.inspectionSubmittedDate) {
            const inspectionSubmittedMoment = moment(formState.inspectionSubmittedDate, 'YYYY-MM-DDTHH:mm:ss', true);
            const inspectedConductMoment = moment(formState.inspectedConductOn, 'YYYY-MM-DDTHH:mm:ss', true);
            const currentMoment = moment();

            if (inspectionSubmittedMoment.isAfter(currentMoment, 'day')) {
                newErrors.inspectionSubmittedDate = 'Submitted date for Inspection cannot be in the future.';
            } else if (
                formState.inspectedConductOn &&
                inspectedConductMoment.isValid() &&
                inspectionSubmittedMoment.isAfter(inspectedConductMoment, 'day')
            ) {
                newErrors.inspectionSubmittedDate =
                    'Submitted date for Inspection must not be after the Inspection Conducted date.';
            }
        }

        // Finalize errors and validation result
        if (Object.values(newErrors).some((error) => error)) {
            setErrors(newErrors);
            return false; // Validation failed
        }

        setErrors({}); // Clear all errors if validation passed
        return true; // Validation passed
    };


    const handleSubmit = async () => {
        // Call the validateFields function to validate all fields
        const isValid = await validateFields();
        console.log("isValid", isValid)
        // If validation fails, return early
        if (!isValid) {
            return;
        }
        const createPayload = {
            caliblicenseno: formState.calibrationNo,
            calibclassgrade: formState.classGrade,
            isactive: formState.status.id,
            calibinspectedby: formState.inspectedBy,
            calibcontactperson: formState.contactPerson,
            calibsubmitteddate: formatDateForBackend(formState.inspectionSubmittedDate),
            calibinspectiondate: formatDateForBackend(formState.inspectedConductOn),
            calibnextinspdate: formatDateForBackend(formState.nextInspectionDate),
            calibexpirydate: formatDateForBackend(formState.expiryDate),
            calibissuingbody: formState.issuingBody,
            calibissuedate: formatDateForBackend(formState.issueDate),
            calibremarks: formState.remarks,
            categorydesc: formState.calibrationCategory.value,
            masservicedetailsId: formState.service.id,
            facilityid: facilityId,
            isdeleted: false,
            maslicensetypecodedetailsId: formState.calibrationTypeCode.id,
            mascalibrationcertcategoryId: formState?.calibrationCategory.primaryId,
            id: location?.state?.id || "",
            categorydesc: formState.hospitalName.label
        };
        console.log('createPayload', createPayload)
        // API call to create calibration details
        const createResponse = editMode ? await calibrationCertificateRepository.updateCalibrationDetails(location?.state?.id, createPayload) : await calibrationCertificateRepository.createCalibrationDetails(createPayload);
        setIsDirty(false);
        return createResponse;

    };
    useImperativeHandle(ref, () => ({
        handleSubmit,
        validateFields,
        hasUnsavedChanges: () => isDirty,
        hospital
    }));
    useEffect(() => {
        if (props.onServiceChange) {
            props.onServiceChange(formState.service);
        }
    }, [formState.service]);


    return (
        <>
            <PageTitle
                title={location?.state?.title}
            />

            <Box mt={{ xs: 1, md: 2 }}>

                <DefaultCard sx={{ height: "100%" }}>
                    <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                        <Box mb={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={4}>
                                    <CustomSelectInput
                                        disableClearable={true}
                                        label="Service"
                                        isMandatoryField={true}
                                        value={formState.service}
                                        onChange={(value) => handleOnChange(value, "service")}
                                        options={serviceOptions}
                                        id="service"
                                        name="service"
                                        error={!!errors.service}
                                        errorMessage={errors.service}
                                        autoFocus
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Calibration No."
                                        id="Calibration No."
                                        maxlength={60}
                                        value={formState.calibrationNo}
                                        onChange={(e) => handleOnChange(e, "calibrationNo")}
                                        isMandatoryField={true}
                                        error={!!errors.calibrationNo}
                                        errorMessage={errors.calibrationNo}
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomSelectInput
                                        label="Calibration Category"
                                        id="Calibration-category"
                                        name="calibrationCategory"
                                        onChange={(value) => handleOnChange(value, "calibrationCategory")}
                                        options={calibrationcategoryOptions}
                                        isMandatoryField={true}
                                        value={formState.calibrationCategory || ""}
                                        error={!!errors.calibrationCategory}
                                        errorMessage={errors.calibrationCategory}
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>


                                {formState.calibrationCategory.value === 'Hospital' && (
                                    <Grid item xs={6} md={4}>
                                        <CustomSelectInput
                                            label="Hospital Name"
                                            id="hospital-name"
                                            value={formState.hospitalName}
                                            isMandatoryField={true}
                                            error={!!errors.hospitalName}
                                            errorMessage={errors.hospitalName}
                                            onChange={(value) => handleOnChange(value, "hospitalName")}
                                            options={hospitalNameOptions}
                                            disabled={editMode || viewMode}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={6} md={4}>
                                    <CustomTextFieldWithSearch
                                        label="Calibration Type Code"
                                        id="Calibration Type Code"
                                        name="calibrationTypeCode"
                                        value={formState.calibrationTypeCode}
                                        options={calibrationTypecodeOptions.map(option => ({
                                            label: option.label,
                                            value: option.value,
                                            id: option.id
                                        }))}
                                        onChange={handleOnChange}
                                        isMandatoryField={true}
                                        error={!!errors.calibrationTypeCode}
                                        errorMessage={errors.calibrationTypeCode}
                                        disabled={editMode || viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Calibration Type Code Description"
                                        id="calibrationTypecodeDesc"
                                        placeholder={""}
                                        name="calibrationTypecodeDesc"
                                        disabled
                                        value={formState.calibrationTypeCodeDesc}
                                        isRefilByOtherField={true}
                                    />


                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Class/Grade"
                                        id="class-grade"
                                        maxlength={60}
                                        onChange={(e) => handleOnChange(e, "classGrade")}
                                        value={formState.classGrade}
                                        error={!!errors.classGrade}
                                        errormessages={errors.classGrade}
                                        disabled={viewMode}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>

                                    <CustomSelectInput
                                        disableClearable={true}
                                        label="Status"
                                        id="status"
                                        name="status"
                                        value={formState.status}
                                        onChange={handleOnChange}
                                        options={statusOptions}
                                        defaultOption={statusOptions[0]}
                                        disabled={viewMode}
                                    />

                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Inspected By"
                                        id="inspected-by"
                                        onChange={(e) => handleOnChange(e, "inspectedBy")}
                                        value={formState.inspectedBy}
                                        error={!!errors.inspectedBy}
                                        errormessages={errors.inspectedBy}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Contact Person"
                                        id="contact-person"
                                        onChange={(e) => handleOnChange(e, "contactPerson")}
                                        value={formState.contactPerson}
                                        error={!!errors.contactPerson}
                                        errormessages={errors.contactPerson}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Date Submitted for Inspection"
                                        id="date-for-inspection"
                                        value={formatDateForDisplay(formState.inspectionSubmittedDate)}
                                        onDateChange={(newDate) => handleDateChange('inspectionSubmittedDate', newDate)}
                                        error={!!errors.inspectionSubmittedDate}
                                        errormessages={errors.inspectionSubmittedDate ? 'Submitted date cannot be in the future.' : ''}
                                        maxDate={currentDate}
                                        disabled={viewMode}
                                    />
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Inspection Conducted on"
                                        id="inspection-conducted"
                                        value={formatDateForDisplay(formState.inspectedConductOn)}
                                        onDateChange={(newDate) => handleDateChange('inspectedConductOn', newDate)}
                                        error={!!errors.inspectedConductOn}
                                        errormessages={
                                            errors.inspectedConductOn
                                                ? 'Inspection Conducted date cannot be in the future or on/after the Next Inspection Date.'
                                                : ''
                                        }
                                        maxDate={currentDate}
                                        minDate={formState.inspectionSubmittedDate ? moment(formState.inspectionSubmittedDate).add(1, 'day').format('YYYY-MM-DD') : null}
                                        disabled={viewMode}
                                    />
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Next Inspection Date"
                                        id="next-inspection-date"
                                        value={formatDateForDisplay(formState.nextInspectionDate)}
                                        onDateChange={(newDate) => handleDateChange('nextInspectionDate', newDate)}
                                        error={!!errors.nextInspectionDate}
                                        errormessages={
                                            errors.nextInspectionDate
                                                ? 'Next Inspection Date must be after the Inspection Conducted date.'
                                                : ''
                                        }
                                        minDate={formState.inspectedConductOn ? moment(formState.inspectedConductOn).add(1, 'day').format('YYYY-MM-DD') : null}
                                        disabled={viewMode}
                                    />
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Expiry Date"
                                        id="expiry-date"
                                        value={editMode ? formatDateForDisplay(formState.expiryDate) : formatDateForDisplay(formState.expiryDate)}
                                        onDateChange={(newDate) => handleDateChange('expiryDate', newDate)}
                                        minimumDate={moment().format('DD/MM/YYYY')} // Set the minimum date as today
                                        isMandatoryField={true}
                                        error={!!errors.expiryDate}
                                        errorMessage={errors.expiryDate}
                                        disabled={viewMode}
                                    />

                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomTextField
                                        label="Issuing Body	"
                                        id="issuing-body"
                                        onChange={(e) => handleOnChange(e, "issuingBody")}
                                        value={formState.issuingBody}
                                        disabled={viewMode}

                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <CustomDatePicker
                                        label="Issue Date"
                                        id="issue-date"
                                        value={formatDateForDisplay(formState.issueDate)}
                                        onDateChange={(newDate) => handleDateChange('issueDate', newDate)}
                                        error={!!errors.issueDate}
                                        errormessages={errors.issueDate}
                                        disabled={viewMode} />

                                </Grid>
                                <Grid item xs={6} md={8}>
                                    <CustomTextField
                                        label="Remarks"
                                        id="remarks"
                                        name="remarks"
                                        value={formState.remarks}
                                        onChange={(e) => handleOnChange(e, "remarks")}
                                        error={!!errors.remarks}
                                        errormessages={errors.remarks}
                                        disabled={viewMode} />
                                </Grid>
                            </Grid>

                        </Box>
                        <CustomDialog
                            open={openAlertDialog}
                            onClose={handleCloseAlertDialog}
                            onConfirm={handleCloseAlertDialog}
                            content={alertMessage}
                            confirmBtn="Ok"
                        />



                    </form>
                </DefaultCard>

            </Box>
        </>
    )
});




export default CalibrationCertificateForm;